import { useState, type CSSProperties } from "react";
import { completion, countScale, SERIES, type Bucket, type Counts, type Series } from "./summary";

/** The dashboard's charts, drawn in HTML so they stay crisp and follow the
 * page's width: thin bars from one baseline, a 2px gap between stacked
 * segments, a legend for every chart with more than one series, and the values
 * on hover and keyboard focus as well as in the tables below. */

const SERIES_CLASS: Record<Series, string> = {
  done: "vd-done",
  not_done: "vd-missed",
  overdue: "vd-overdue",
  pending: "vd-pending",
};

export function Legend({ series = SERIES }: { series?: readonly { key: Series; label: string }[] }) {
  return (
    <ul className="vd-legend" aria-label="Key">
      {series.map((s) => (
        <li key={s.key}>
          <span className={`vd-swatch ${SERIES_CLASS[s.key]}`} aria-hidden="true" />
          {s.label}
        </li>
      ))}
    </ul>
  );
}

function describe(label: string, counts: Counts): string {
  const parts = SERIES.filter((s) => counts[s.key] > 0).map((s) => `${counts[s.key]} ${s.label.toLowerCase()}`);
  return `${label}: ${counts.planned} planned${parts.length > 0 ? `, ${parts.join(", ")}` : ""}`;
}

/** The hover card: the value leads, the series follows, each keyed by a short
 * stroke of its colour. */
function Readout({ title, counts, style }: { title: string; counts: Counts; style?: CSSProperties }) {
  return (
    <div className="vd-tooltip" role="status" style={style}>
      <div className="vd-tooltip-title">{title}</div>
      {SERIES.filter((s) => counts[s.key] > 0).map((s) => (
        <div key={s.key} className="vd-tooltip-row">
          <span className={`vd-key ${SERIES_CLASS[s.key]}`} aria-hidden="true" />
          <strong>{counts[s.key]}</strong>
          <span>{s.label}</span>
        </div>
      ))}
      <div className="vd-tooltip-row vd-tooltip-total">
        <span className="vd-key" aria-hidden="true" />
        <strong>{counts.planned}</strong>
        <span>planned{completion(counts) === null ? "" : ` · ${completion(counts)}% done`}</span>
      </div>
    </div>
  );
}

/** Stacked segments, bottom (or left) first. Empty series draw nothing. */
function Segments({ counts }: { counts: Counts }) {
  return (
    <>
      {SERIES.filter((s) => counts[s.key] > 0).map((s) => (
        <span key={s.key} className={`vd-seg ${SERIES_CLASS[s.key]}`} style={{ flexGrow: counts[s.key] }} />
      ))}
    </>
  );
}

/** Visits per day (or week) as stacked columns. A column narrows the list of
 * visits below to its day; pressing it again lets go. */
export function Columns({
  buckets,
  selected,
  onSelect,
}: {
  buckets: readonly Bucket[];
  selected: string | null;
  onSelect: (bucket: Bucket) => void;
}) {
  const [hover, setHover] = useState<number | null>(null);
  const { top, ticks } = countScale(Math.max(0, ...buckets.map((b) => b.counts.planned)));
  // Thin the axis labels so they never collide: about a dozen at most.
  const every = Math.max(1, Math.ceil(buckets.length / 12));
  const shown = hover === null ? null : buckets[hover];
  return (
    <div className="vd-columns">
      <div className="vd-plot-area">
        <div className="vd-gridlines" aria-hidden="true">
          {ticks.map((tick) => (
            <div key={tick} className="vd-gridline" style={{ bottom: `${(100 * tick) / top}%` }}>
              <span>{tick}</span>
            </div>
          ))}
        </div>
        <div className="vd-plot" style={{ gridTemplateColumns: `repeat(${buckets.length}, minmax(0, 1fr))` }}>
          {buckets.map((bucket, index) => (
            <button
              key={bucket.key}
              type="button"
              className="vd-col"
              aria-pressed={selected === bucket.key}
              aria-label={describe(bucket.label, bucket.counts)}
              onClick={() => onSelect(bucket)}
              onPointerEnter={() => setHover(index)}
              onPointerLeave={() => setHover(null)}
              onFocus={() => setHover(index)}
              onBlur={() => setHover(null)}
            >
              <span className="vd-stack" style={{ height: `${(100 * bucket.counts.planned) / top}%` }}>
                {/* First in the markup so the top segment stays the last child, the
                    one that takes the rounded end. */}
                {bucket.counts.planned > 0 ? (
                  <span className="vd-cap" aria-hidden="true">
                    {bucket.counts.planned}
                  </span>
                ) : null}
                <Segments counts={bucket.counts} />
              </span>
            </button>
          ))}
        </div>
        {shown && hover !== null ? (
          <Readout
            title={shown.label}
            counts={shown.counts}
            style={{
              left: `${(100 * (hover + 0.5)) / buckets.length}%`,
              // Beside the column, on the side with room, so it never hides the bar it describes.
              transform: hover >= buckets.length / 2 ? "translateX(calc(-100% - 18px))" : "translateX(18px)",
            }}
          />
        ) : null}
      </div>
      <div className="vd-xaxis" style={{ gridTemplateColumns: `repeat(${buckets.length}, minmax(0, 1fr))` }} aria-hidden="true">
        {buckets.map((bucket, index) => (
          <span key={bucket.key}>{index % every === 0 ? bucket.label.replace(/^Week of /, "") : ""}</span>
        ))}
      </div>
    </div>
  );
}

/** One stacked bar per crew or region, as long as its visits; the share done
 * is written at the end. */
export function StackedBars({
  buckets,
  label,
  onSelect,
  selected,
}: {
  buckets: readonly Bucket[];
  label: string;
  onSelect: (bucket: Bucket) => void;
  selected: string | null;
}) {
  const [hover, setHover] = useState<number | null>(null);
  const most = Math.max(1, ...buckets.map((b) => b.counts.planned));
  return (
    <div className="vd-hbars" role="list" aria-label={label}>
      {buckets.map((bucket, index) => {
        const done = completion(bucket.counts);
        const bar = (
          <span className="vd-hbar-track">
            <span className="vd-hbar" style={{ width: `${(100 * bucket.counts.planned) / most}%` }}>
              <Segments counts={bucket.counts} />
            </span>
            <span className="vd-hbar-value">
              {bucket.counts.done} of {bucket.counts.planned}
              {done === null ? "" : ` · ${done}%`}
            </span>
          </span>
        );
        return (
          <div key={bucket.key} className="vd-hbar-row" role="listitem">
            <button
              type="button"
              className="vd-hbar-button"
              aria-pressed={selected === bucket.key}
              aria-label={describe(bucket.label, bucket.counts)}
              onClick={() => onSelect(bucket)}
              onPointerEnter={() => setHover(index)}
              onPointerLeave={() => setHover(null)}
              onFocus={() => setHover(index)}
              onBlur={() => setHover(null)}
            >
              <span className="vd-hbar-name">{bucket.label}</span>
              {bar}
            </button>
            {hover === index ? <Readout title={bucket.label} counts={bucket.counts} /> : null}
          </div>
        );
      })}
    </div>
  );
}

export interface CountItem {
  key: string;
  label: string;
  count: number;
  /** Written after the count, as "· 80%". */
  note?: string;
}

/** One bar per item, one colour, the count at its tip: no legend, because the
 * card's title says what is counted. */
export function CountBars({
  items,
  label,
  tone,
  max,
  onSelect,
  selected,
}: {
  items: readonly CountItem[];
  label: string;
  tone: "missed" | "progress";
  /** The count a full bar stands for; the largest item when left out. */
  max?: number;
  onSelect?: (item: CountItem) => void;
  selected?: string | null;
}) {
  const full = Math.max(1, max ?? Math.max(0, ...items.map((item) => item.count)));
  return (
    <div className={`vd-hbars vd-count-${tone}`} role="list" aria-label={label}>
      {items.map((item) => {
        const body = (
          <>
            <span className="vd-hbar-name">{item.label}</span>
            <span className="vd-hbar-track">
              <span className="vd-hbar" style={{ width: `${(100 * item.count) / full}%` }}>
                {item.count > 0 ? <span className="vd-seg vd-single" style={{ flexGrow: 1 }} /> : null}
              </span>
              <span className="vd-hbar-value">
                {item.count}
                {item.note ? ` · ${item.note}` : ""}
              </span>
            </span>
          </>
        );
        return (
          <div key={item.key} className="vd-hbar-row" role="listitem">
            {onSelect ? (
              <button
                type="button"
                className="vd-hbar-button"
                aria-pressed={selected === item.key}
                aria-label={`${item.label}: ${item.count}${item.note ? `, ${item.note}` : ""}`}
                onClick={() => onSelect(item)}
              >
                {body}
              </button>
            ) : (
              <div className="vd-hbar-button">{body}</div>
            )}
          </div>
        );
      })}
    </div>
  );
}
